import React, { useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileJson, Upload, Download, Link, CheckCircle, Plus } from "lucide-react";

export default function OpenAPIImportPage() {
  const [url, setUrl] = useState("");
  const [specJson, setSpecJson] = useState("");
  const [mode, setMode] = useState("url");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = React.useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    setSpecJson(text);
    setMode("json");
  };

  const doImport = async () => {
    setImporting(true); setError(""); setResult(null);
    try {
      const body = mode === "url" ? { url, create_definitions: true } : { spec_json: specJson, create_definitions: true };
      const res = await client.post("/openapi/import", body);
      setResult(res.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setImporting(false); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto fade-in">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1"><FileJson size={18} className="text-purple-600" /><h1 className="text-2xl font-bold text-gray-900">Import from OpenAPI</h1></div>
        <p className="text-gray-500 text-sm">Paste a Swagger/OpenAPI 3.0 spec URL or upload the JSON/YAML file to auto-generate API definitions</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        {/* Mode tabs */}
        <div className="flex gap-2 mb-4">
          <button className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${mode === "url" ? "bg-[#006080] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} onClick={() => setMode("url")}>
            <Link size={13} className="inline mr-1.5" /> URL
          </button>
          <button className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${mode === "json" ? "bg-[#006080] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} onClick={() => setMode("json")}>
            <FileJson size={13} className="inline mr-1.5" /> JSON/YAML
          </button>
          <button className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${mode === "file" ? "bg-[#006080] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} onClick={() => { setMode("file"); fileRef.current?.click(); }}>
            <Upload size={13} className="inline mr-1.5" /> Upload File
          </button>
          <input ref={fileRef} type="file" accept=".json,.yaml,.yml" className="hidden" onChange={handleFile} />
        </div>

        {mode === "url" && (
          <div><Label className="text-xs text-gray-500">OpenAPI Spec URL</Label><Input className="mt-1" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://petstore.swagger.io/v2/swagger.json" /></div>
        )}
        {(mode === "json" || mode === "file") && (
          <div><Label className="text-xs text-gray-500">OpenAPI JSON / YAML</Label><textarea className="w-full h-40 px-3 py-2 text-xs font-mono border border-gray-200 rounded-md mt-1 focus:outline-none focus:border-[#006080] resize-none" value={specJson} onChange={e => setSpecJson(e.target.value)} placeholder='{"openapi": "3.0.0", "info": {...}, "paths": {...}}' /></div>
        )}

        {error && <div className="mt-3 p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}

        <Button className="mt-4 bg-[#006080] hover:bg-[#004d66] text-white w-full" onClick={doImport} disabled={importing || (!url && !specJson)}>
          {importing ? "Importing..." : "Import API Definitions"}
        </Button>
      </div>

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={18} className="text-green-600" />
            <p className="font-semibold text-green-700">Import successful!</p>
          </div>
          <div className="flex gap-4 text-sm text-green-600">
            <span><strong>{result.parsed}</strong> operations found</span>
            <span><strong>{result.created}</strong> API definitions created</span>
          </div>
          {result.preview?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-xs text-gray-500 font-semibold">Preview (first 3):</p>
              {result.preview.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="px-1.5 py-0.5 rounded text-white text-[10px] font-bold" style={{ background: d.method === "GET" ? "#059669" : d.method === "POST" ? "#2563EB" : "#D97706" }}>{d.method}</span>
                  <span className="font-medium text-gray-700">{d.name}</span>
                  <span className="font-mono text-gray-400">{d.base_url}{d.path}</span>
                </div>
              ))}
            </div>
          )}
          <Button size="sm" variant="outline" className="mt-3 border-green-300 text-green-700" onClick={() => window.location.href = "/definitions"}>
            View in API Definitions →
          </Button>
        </div>
      )}

      <div className="mt-6 bg-[#F8F9FA] border border-gray-200 rounded-lg p-4">
        <p className="text-xs font-semibold text-gray-600 mb-2">Try these public OpenAPI specs</p>
        <div className="space-y-1">
          {[
            ["Petstore (Swagger sample)", "https://petstore3.swagger.io/api/v3/openapi.json"],
            ["GitHub REST API", "https://raw.githubusercontent.com/github/rest-api-description/main/descriptions/api.github.com/api.github.com.json"],
          ].map(([name, u]) => (
            <button key={u} className="text-xs text-[#006080] hover:underline block" onClick={() => { setUrl(u); setMode("url"); }}>
              {name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
